import { useCallback, useEffect, useRef } from 'react';
import type { LearningMaterial } from '@common/domain/learning-workspace';
import { LearningMaterialMarkdown } from './LearningMaterialMarkdown';
import './LearningMaterialReader.css';

type ProgressMilestone = 25 | 50 | 75 | 100;

interface LearningMaterialReaderProps {
  fillAvailableHeight?: boolean;
  material: LearningMaterial;
  onInteraction?: () => void;
  onProgressMilestone?: (milestone: ProgressMilestone) => void;
}

const PROGRESS_MILESTONES: readonly ProgressMilestone[] = [25, 50, 75, 100];

export function LearningMaterialReader({
  fillAvailableHeight = false,
  material,
  onInteraction,
  onProgressMilestone,
}: LearningMaterialReaderProps) {
  const scrollContainerRef = useRef<HTMLElement>(null);
  const pointerFocusRef = useRef(false);
  const emittedMilestonesBySlugRef = useRef(new Map<string, Set<ProgressMilestone>>());

  const emitMilestone = useCallback((milestone: ProgressMilestone) => {
    let emittedMilestones = emittedMilestonesBySlugRef.current.get(material.slug);
    if (!emittedMilestones) {
      emittedMilestones = new Set<ProgressMilestone>();
      emittedMilestonesBySlugRef.current.set(material.slug, emittedMilestones);
    }
    if (emittedMilestones.has(milestone)) {
      return;
    }

    emittedMilestones.add(milestone);
    onProgressMilestone?.(milestone);
  }, [material.slug, onProgressMilestone]);

  const updateScrollProgress = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || container.scrollHeight <= container.clientHeight) {
      return;
    }

    const percentage = (100 * container.scrollTop) / Math.max(1, container.scrollHeight - container.clientHeight);
    PROGRESS_MILESTONES.forEach((milestone) => {
      if (percentage >= milestone) {
        emitMilestone(milestone);
      }
    });
  }, [emitMilestone]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      updateScrollProgress();
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [updateScrollProgress]);

  const handleMaterialInteraction = () => {
    onInteraction?.();
    const container = scrollContainerRef.current;
    if (container && container.scrollHeight <= container.clientHeight) {
      emitMilestone(100);
    }
  };

  const handlePointerDown = () => {
    pointerFocusRef.current = true;
    handleMaterialInteraction();
  };

  const handlePointerEnd = () => {
    pointerFocusRef.current = false;
  };

  const handleFocus = () => {
    if (!pointerFocusRef.current) handleMaterialInteraction();
  };

  return (
    <section
      ref={scrollContainerRef}
      aria-label={material.title}
      className="learning-material-reader"
      onFocus={handleFocus}
      onKeyDown={handleMaterialInteraction}
      onPointerCancel={handlePointerEnd}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerEnd}
      onWheel={handleMaterialInteraction}
      style={fillAvailableHeight ? {
        flex: '1 1 auto',
        minHeight: 0,
        maxHeight: '100%',
        overflowY: 'auto',
      } : undefined}
      tabIndex={0}
    >
      <LearningMaterialMarkdown contentMarkdown={material.contentMarkdown} />
    </section>
  );
}
